/**
 * FieldRendererRegistry — Maps field interface types to React components.
 *
 * This is the core of the schema-driven UI. When schema metadata (from
 * axon-data or Directus) returns field definitions with a `meta.interface`
 * value, this registry determines which React component renders that field.
 *
 * Works with both:
 * - Directus field metadata (DirectusField with meta.interface)
 * - AxonBOS SQL schema (mapped to DirectusField format by useSchema)
 *
 * SQL type → interface mapping happens in useSchema.ts:
 *   INT/BIGINT → 'input' (integer)
 *   VARCHAR → 'input' (string)
 *   TEXT → 'input-multiline'
 *   BOOLEAN/BIT → 'boolean'
 *   DATE → 'datetime'
 *   JSON/JSONB → 'input-code'
 *   FK columns → 'select-dropdown-m2o'
 *
 * Adding a new field type = one line in the registry + one React component.
 */

import * as React from 'react';
import type { DirectusField, DirectusFieldOption } from '@yesbos/api-client';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { FileUpload, type UploadedFile } from './file-upload';

// ─── Field Renderer Props ──────────────────────────────────────────

export interface FieldRendererProps {
  /** Directus field definition with full metadata */
  field: DirectusField;
  /** Current value */
  value: unknown;
  /** Change handler */
  onChange: (value: unknown) => void;
  /** Error message from validation */
  error?: string;
  /** Read-only mode (view pages) */
  readOnly?: boolean;
  /** Disabled state */
  disabled?: boolean;
}

// ─── Individual Field Components ───────────────────────────────────

function TextInput({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  if (readOnly) {
    return <ReadOnlyValue value={value} />;
  }

  return (
    <Input
      type="text"
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.meta?.note ?? `Enter ${formatLabel(field.field)}`}
      disabled={disabled || field.meta?.readonly}
      className={cn(error && 'border-[var(--color-destructive)]')}
    />
  );
}

function NumberInput({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  if (readOnly) {
    return <ReadOnlyValue value={value} />;
  }

  return (
    <Input
      type="number"
      value={value !== null && value !== undefined ? String(value) : ''}
      onChange={(e) => {
        const num = e.target.value === '' ? null : Number(e.target.value);
        onChange(num);
      }}
      placeholder={field.meta?.note ?? `Enter ${formatLabel(field.field)}`}
      disabled={disabled || field.meta?.readonly}
      className={cn(error && 'border-[var(--color-destructive)]')}
    />
  );
}

function EmailInput({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  if (readOnly) {
    return (
      <a
        href={`mailto:${value}`}
        className="text-[var(--color-primary)] hover:underline"
      >
        {String(value ?? '')}
      </a>
    );
  }

  return (
    <Input
      type="email"
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.meta?.note ?? 'email@example.com'}
      disabled={disabled || field.meta?.readonly}
      className={cn(error && 'border-[var(--color-destructive)]')}
    />
  );
}

function PhoneInput({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  if (readOnly) {
    return (
      <a
        href={`tel:${value}`}
        className="text-[var(--color-primary)] hover:underline"
      >
        {String(value ?? '')}
      </a>
    );
  }

  return (
    <Input
      type="tel"
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.meta?.note ?? '+91 XXXXX XXXXX'}
      disabled={disabled || field.meta?.readonly}
      className={cn(error && 'border-[var(--color-destructive)]')}
    />
  );
}

function MultilineInput({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  if (readOnly) {
    return (
      <p className="text-sm whitespace-pre-wrap">{String(value ?? '')}</p>
    );
  }

  return (
    <Textarea
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.meta?.note ?? `Enter ${formatLabel(field.field)}`}
      disabled={disabled || field.meta?.readonly}
      rows={4}
      className={cn(error && 'border-[var(--color-destructive)]')}
    />
  );
}

function DateInput({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  if (readOnly) {
    const formatted = value
      ? new Date(value as string).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })
      : '—';
    return <ReadOnlyValue value={formatted} />;
  }

  // Extract date portion for date input
  const dateValue = value
    ? (value as string).substring(0, 10)
    : '';

  return (
    <Input
      type="date"
      value={dateValue}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled || field.meta?.readonly}
      className={cn(error && 'border-[var(--color-destructive)]')}
    />
  );
}

function DateTimeInput({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  if (readOnly) {
    const formatted = value
      ? new Date(value as string).toLocaleString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';
    return <ReadOnlyValue value={formatted} />;
  }

  // Convert ISO to datetime-local format
  const dtValue = value
    ? (value as string).substring(0, 16)
    : '';

  return (
    <Input
      type="datetime-local"
      value={dtValue}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled || field.meta?.readonly}
      className={cn(error && 'border-[var(--color-destructive)]')}
    />
  );
}

function BooleanInput({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  if (readOnly) {
    return (
      <span className={cn(
        'inline-flex items-center gap-1.5 text-sm',
        value ? 'text-[var(--color-success)]' : 'text-[var(--color-muted-foreground)]',
      )}>
        {value ? 'Yes' : 'No'}
      </span>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      <Checkbox
        checked={!!value}
        onCheckedChange={(checked) => onChange(checked)}
        disabled={disabled || field.meta?.readonly}
      />
      <Label className="text-sm font-normal">
        {field.meta?.note ?? formatLabel(field.field)}
      </Label>
    </div>
  );
}

function SelectDropdown({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  const options = (field.meta?.options as any)?.choices as DirectusFieldOption[] ?? [];

  if (readOnly) {
    const selected = options.find((o) => String(o.value) === String(value));
    return <ReadOnlyValue value={selected?.text ?? String(value ?? '—')} />;
  }

  return (
    <Select
      value={value !== null && value !== undefined ? String(value) : undefined}
      onValueChange={(val) => onChange(val)}
      disabled={disabled || field.meta?.readonly}
    >
      <SelectTrigger className={cn(error && 'border-[var(--color-destructive)]')}>
        <SelectValue placeholder={`Select ${formatLabel(field.field)}`} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={String(opt.value)} value={String(opt.value)}>
            {opt.text}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ─── FIX 2: M2O Relational Select (async searchable dropdown) ────

interface RelationalOption {
  id: string | number;
  display: string;
}

/**
 * M2O relational field — renders as a searchable dropdown that
 * fetches options from the related collection endpoint.
 *
 * The related collection is determined from:
 * - field.schema.foreign_key_table (Directus schema)
 * - field.meta.options.relatedCollection (explicit config)
 *
 * The API endpoint pattern: GET /items/{collection}?search={term}&limit=25
 * Expected response: { data: [{ id, ...displayFields }] }
 */
function ManyToOneSelect({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [options, setOptions] = React.useState<RelationalOption[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const [selectedDisplay, setSelectedDisplay] = React.useState<string>('');
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Derive related collection from field metadata
  const relatedCollection =
    (field.meta?.options as any)?.relatedCollection ??
    field.schema?.foreign_key_table ??
    '';

  // Display field from options or fallback to common patterns
  const displayTemplate =
    (field.meta?.options as any)?.template ??
    (field.meta?.display_options as any)?.template ??
    '';

  // Resolve display text for the current value
  React.useEffect(() => {
    if (value === null || value === undefined || value === '') {
      setSelectedDisplay('');
      return;
    }
    // If value is an object (populated relation), extract display
    if (typeof value === 'object' && value !== null) {
      const obj = value as Record<string, unknown>;
      const display =
        obj.name ?? obj.title ?? obj.label ?? obj.display_name ?? obj.id;
      setSelectedDisplay(String(display ?? value));
      return;
    }
    // Value is just an ID — show it as-is until fetched
    setSelectedDisplay(String(value));
  }, [value]);

  // Debounced search for related records
  const handleSearch = React.useCallback(
    (term: string) => {
      setSearchTerm(term);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (!relatedCollection) return;

      debounceRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          // Build API URL for related collection search
          const params = new URLSearchParams({
            limit: '25',
            fields: '*',
          });
          if (term) {
            params.set('search', term);
          }

          const response = await fetch(
            `/items/${relatedCollection}?${params.toString()}`,
          );
          if (!response.ok) {
            setOptions([]);
            return;
          }

          const json = await response.json();
          const items: Record<string, unknown>[] = json.data ?? json ?? [];

          const mapped: RelationalOption[] = items.map((item) => {
            const id = item.id ?? item.ID ?? '';
            // Build display text from template or common fields
            let display: string;
            if (displayTemplate) {
              display = displayTemplate.replace(
                /\{\{(\w+)\}\}/g,
                (_: string, key: string) => String(item[key] ?? ''),
              );
            } else {
              display = String(
                item.name ??
                  item.title ??
                  item.label ??
                  item.display_name ??
                  id,
              );
            }
            return { id, display };
          });

          setOptions(mapped);
        } catch {
          setOptions([]);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    },
    [relatedCollection, displayTemplate],
  );

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load initial options when opened
  React.useEffect(() => {
    if (isOpen && options.length === 0) {
      handleSearch('');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (readOnly) {
    return <ReadOnlyValue value={selectedDisplay || value} />;
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Search input that doubles as display */}
      <Input
        type="text"
        value={isOpen ? searchTerm : selectedDisplay}
        onChange={(e) => {
          handleSearch(e.target.value);
          if (!isOpen) setIsOpen(true);
        }}
        onFocus={() => {
          setIsOpen(true);
          setSearchTerm('');
        }}
        placeholder={`Search ${formatLabel(field.field)}...`}
        disabled={disabled || field.meta?.readonly}
        className={cn(error && 'border-[var(--color-destructive)]')}
      />

      {/* Clear button */}
      {value !== null && value !== undefined && value !== '' && !disabled && (
        <button
          type="button"
          onClick={() => {
            onChange(null);
            setSelectedDisplay('');
            setSearchTerm('');
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] text-xs"
          aria-label="Clear selection"
        >
          x
        </button>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-popover)] shadow-md max-h-60 overflow-auto">
          {isSearching ? (
            <div className="px-3 py-2 text-sm text-[var(--color-muted-foreground)]">
              Searching...
            </div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[var(--color-muted-foreground)]">
              {relatedCollection
                ? 'No results found'
                : 'No related collection configured'}
            </div>
          ) : (
            options.map((opt) => (
              <button
                key={String(opt.id)}
                type="button"
                onClick={() => {
                  onChange(opt.id);
                  setSelectedDisplay(opt.display);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
                className={cn(
                  'flex w-full items-center px-3 py-2 text-sm text-left',
                  'hover:bg-[var(--color-accent)] transition-colors',
                  String(opt.id) === String(value) &&
                    'bg-[var(--color-primary)]/10 text-[var(--color-primary)]',
                )}
              >
                <span className="truncate">{opt.display}</span>
                <span className="ml-auto text-xs text-[var(--color-muted-foreground)]">
                  #{String(opt.id)}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * M2M relational field — renders as a multi-select with tag chips.
 *
 * Selected items shown as removable badges/chips above the search input.
 * Uses the same API pattern as M2O for fetching options.
 */
function ManyToManySelect({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [options, setOptions] = React.useState<RelationalOption[]>([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSearching, setIsSearching] = React.useState(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Derive related collection from field metadata
  const relatedCollection =
    (field.meta?.options as any)?.relatedCollection ??
    field.schema?.foreign_key_table ??
    '';

  const displayTemplate =
    (field.meta?.options as any)?.template ??
    (field.meta?.display_options as any)?.template ??
    '';

  // Normalize current value to array of selected items
  const selectedItems: RelationalOption[] = React.useMemo(() => {
    if (!value) return [];
    const arr = Array.isArray(value) ? value : [value];
    return arr.map((item) => {
      if (typeof item === 'object' && item !== null) {
        const obj = item as Record<string, unknown>;
        const id = obj.id ?? obj.ID ?? '';
        const display = String(
          obj.name ?? obj.title ?? obj.label ?? obj.display_name ?? id,
        );
        return { id, display };
      }
      return { id: item, display: String(item) };
    });
  }, [value]);

  const selectedIds = React.useMemo(
    () => new Set(selectedItems.map((s) => String(s.id))),
    [selectedItems],
  );

  // Debounced search
  const handleSearch = React.useCallback(
    (term: string) => {
      setSearchTerm(term);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (!relatedCollection) return;

      debounceRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          const params = new URLSearchParams({
            limit: '25',
            fields: '*',
          });
          if (term) {
            params.set('search', term);
          }

          const response = await fetch(
            `/items/${relatedCollection}?${params.toString()}`,
          );
          if (!response.ok) {
            setOptions([]);
            return;
          }

          const json = await response.json();
          const items: Record<string, unknown>[] = json.data ?? json ?? [];

          const mapped: RelationalOption[] = items.map((item) => {
            const id = item.id ?? item.ID ?? '';
            let display: string;
            if (displayTemplate) {
              display = displayTemplate.replace(
                /\{\{(\w+)\}\}/g,
                (_: string, key: string) => String(item[key] ?? ''),
              );
            } else {
              display = String(
                item.name ??
                  item.title ??
                  item.label ??
                  item.display_name ??
                  id,
              );
            }
            return { id, display };
          });

          setOptions(mapped);
        } catch {
          setOptions([]);
        } finally {
          setIsSearching(false);
        }
      }, 300);
    },
    [relatedCollection, displayTemplate],
  );

  // Toggle an item's selection
  const toggleItem = React.useCallback(
    (opt: RelationalOption) => {
      const currentIds = selectedItems.map((s) => s.id);
      if (selectedIds.has(String(opt.id))) {
        // Remove
        onChange(currentIds.filter((id) => String(id) !== String(opt.id)));
      } else {
        // Add
        onChange([...currentIds, opt.id]);
      }
    },
    [selectedItems, selectedIds, onChange],
  );

  const removeItem = React.useCallback(
    (id: string | number) => {
      const currentIds = selectedItems.map((s) => s.id);
      onChange(currentIds.filter((cid) => String(cid) !== String(id)));
    },
    [selectedItems, onChange],
  );

  // Close dropdown on outside click
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load initial options when opened
  React.useEffect(() => {
    if (isOpen && options.length === 0) {
      handleSearch('');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (readOnly) {
    if (selectedItems.length === 0) {
      return <ReadOnlyValue value="—" />;
    }
    return (
      <div className="flex flex-wrap gap-1 py-1">
        {selectedItems.map((item) => (
          <Badge key={String(item.id)} variant="secondary" className="text-xs">
            {item.display}
          </Badge>
        ))}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Selected chips */}
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedItems.map((item) => (
            <Badge
              key={String(item.id)}
              variant="secondary"
              className="text-xs gap-1 pr-1"
            >
              {item.display}
              <button
                type="button"
                onClick={() => removeItem(item.id)}
                className="ml-0.5 rounded-full hover:bg-[var(--color-destructive)]/20 p-0.5"
                aria-label={`Remove ${item.display}`}
              >
                <span className="text-[10px] leading-none">x</span>
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search input */}
      <Input
        type="text"
        value={searchTerm}
        onChange={(e) => {
          handleSearch(e.target.value);
          if (!isOpen) setIsOpen(true);
        }}
        onFocus={() => {
          setIsOpen(true);
          if (options.length === 0) handleSearch('');
        }}
        placeholder={`Search ${formatLabel(field.field)}...`}
        disabled={disabled || field.meta?.readonly}
        className={cn(error && 'border-[var(--color-destructive)]')}
      />

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-popover)] shadow-md max-h-60 overflow-auto">
          {isSearching ? (
            <div className="px-3 py-2 text-sm text-[var(--color-muted-foreground)]">
              Searching...
            </div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-[var(--color-muted-foreground)]">
              {relatedCollection
                ? 'No results found'
                : 'No related collection configured'}
            </div>
          ) : (
            options.map((opt) => {
              const isSelected = selectedIds.has(String(opt.id));
              return (
                <button
                  key={String(opt.id)}
                  type="button"
                  onClick={() => toggleItem(opt)}
                  className={cn(
                    'flex w-full items-center px-3 py-2 text-sm text-left',
                    'hover:bg-[var(--color-accent)] transition-colors',
                    isSelected &&
                      'bg-[var(--color-primary)]/10 text-[var(--color-primary)]',
                  )}
                >
                  <Checkbox
                    checked={isSelected}
                    className="mr-2 pointer-events-none"
                    tabIndex={-1}
                  />
                  <span className="truncate">{opt.display}</span>
                  <span className="ml-auto text-xs text-[var(--color-muted-foreground)]">
                    #{String(opt.id)}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ─── FIX 3: File Upload Field ────────────────────────────────────

/**
 * FileUploadField — Wraps the FileUpload component for use in FieldRenderer.
 *
 * Adapts between the field value format (Directus file ID or array of IDs)
 * and the FileUpload component's UploadedFile[] format.
 *
 * - Single file fields ('file', 'file-image'): value is a file ID or object
 * - Multiple file fields ('files'): value is an array of file IDs or objects
 */
function FileUploadField({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  const interfaceType = field.meta?.interface ?? '';
  const isMultiple = interfaceType === 'files';
  const isImage = interfaceType === 'file-image';

  // Convert field value to UploadedFile[] format
  const files: UploadedFile[] = React.useMemo(() => {
    if (!value) return [];

    const items = Array.isArray(value) ? value : [value];
    return items.map((item, idx) => {
      if (typeof item === 'object' && item !== null) {
        const obj = item as Record<string, unknown>;
        return {
          id: String(obj.id ?? `file-${idx}`),
          name: String(obj.filename_download ?? obj.title ?? obj.name ?? 'File'),
          type: String(obj.type ?? 'application/octet-stream'),
          size: Number(obj.filesize ?? 0),
          previewUrl:
            typeof obj.url === 'string'
              ? obj.url
              : typeof obj.id === 'string'
                ? `/assets/${obj.id}`
                : undefined,
        };
      }
      // Just an ID string
      return {
        id: String(item),
        name: String(item),
        type: isImage ? 'image/*' : 'application/octet-stream',
        size: 0,
        previewUrl: isImage ? `/assets/${item}` : undefined,
      };
    });
  }, [value, isImage]);

  if (readOnly) {
    if (files.length === 0) {
      return <ReadOnlyValue value="No files" />;
    }
    return (
      <div className="space-y-1 py-1">
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-2 text-sm">
            {f.previewUrl && isImage ? (
              <img
                src={f.previewUrl}
                alt={f.name}
                className="h-10 w-10 rounded object-cover"
              />
            ) : null}
            <span className="text-[var(--color-foreground)]">{f.name}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <FileUpload
      value={files}
      onChange={(newFiles) => {
        if (isMultiple) {
          // Return array of file objects (with raw File for upload handling)
          onChange(newFiles.map((f) => f.file ?? f.id));
        } else {
          // Single file — return the first file or null
          onChange(newFiles.length > 0 ? (newFiles[0].file ?? newFiles[0].id) : null);
        }
      }}
      accept={isImage ? 'image/*' : undefined}
      multiple={isMultiple}
      maxSizeMB={
        (field.meta?.options as any)?.maxSizeMB ?? 10
      }
      label={`Upload ${formatLabel(field.field)}`}
      error={error}
      disabled={disabled || field.meta?.readonly}
    />
  );
}

// ─── FIX 4: Rich Text / Markdown Editing ─────────────────────────

/**
 * MarkdownTextarea — A textarea with markdown syntax support.
 *
 * No external rich text editor dependency required. Provides:
 * - Toolbar buttons for bold, italic, bullet/numbered lists
 * - Keyboard shortcuts (Ctrl+B, Ctrl+I)
 * - Read-only mode with basic markdown rendering
 *   (bold, italic, lists rendered safely via regex)
 */
function RichTextMarkdown({ field, value, onChange, error, readOnly, disabled }: FieldRendererProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const text = (value as string) ?? '';

  // Insert markdown syntax around selection or at cursor
  const wrapSelection = React.useCallback(
    (before: string, after: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selected = text.substring(start, end);
      const newText =
        text.substring(0, start) +
        before +
        selected +
        after +
        text.substring(end);
      onChange(newText);

      // Restore cursor position after React re-render
      requestAnimationFrame(() => {
        textarea.selectionStart = start + before.length;
        textarea.selectionEnd = end + before.length;
        textarea.focus();
      });
    },
    [text, onChange],
  );

  // Insert a prefix at the start of the current line
  const insertLinePrefix = React.useCallback(
    (prefix: string) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const start = textarea.selectionStart;
      // Find the start of the current line
      const lineStart = text.lastIndexOf('\n', start - 1) + 1;
      const newText =
        text.substring(0, lineStart) +
        prefix +
        text.substring(lineStart);
      onChange(newText);

      requestAnimationFrame(() => {
        const newPos = start + prefix.length;
        textarea.selectionStart = newPos;
        textarea.selectionEnd = newPos;
        textarea.focus();
      });
    },
    [text, onChange],
  );

  // Keyboard shortcuts
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'b') {
          e.preventDefault();
          wrapSelection('**', '**');
        } else if (e.key === 'i') {
          e.preventDefault();
          wrapSelection('*', '*');
        }
      }
    },
    [wrapSelection],
  );

  // Basic markdown to safe HTML for read-only display
  if (readOnly) {
    return <MarkdownReadOnly text={text} />;
  }

  return (
    <div className="space-y-1">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border border-[var(--color-border)] rounded-t-md px-1 py-0.5 bg-[var(--color-muted)]/30">
        <ToolbarButton
          label="Bold (Ctrl+B)"
          onClick={() => wrapSelection('**', '**')}
          disabled={disabled}
        >
          <strong className="text-xs">B</strong>
        </ToolbarButton>
        <ToolbarButton
          label="Italic (Ctrl+I)"
          onClick={() => wrapSelection('*', '*')}
          disabled={disabled}
        >
          <em className="text-xs">I</em>
        </ToolbarButton>
        <div className="w-px h-4 bg-[var(--color-border)] mx-0.5" />
        <ToolbarButton
          label="Bullet list"
          onClick={() => insertLinePrefix('- ')}
          disabled={disabled}
        >
          <span className="text-xs">&#8226; List</span>
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          onClick={() => insertLinePrefix('1. ')}
          disabled={disabled}
        >
          <span className="text-xs">1. List</span>
        </ToolbarButton>
      </div>

      {/* Textarea */}
      <Textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={field.meta?.note ?? `Enter ${formatLabel(field.field)} (supports Markdown)`}
        disabled={disabled || field.meta?.readonly}
        rows={6}
        className={cn(
          'rounded-t-none -mt-1 font-mono text-sm',
          error && 'border-[var(--color-destructive)]',
        )}
      />
    </div>
  );
}

/** Toolbar button for markdown editor */
function ToolbarButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center h-6 px-1.5 rounded text-[var(--color-foreground)]',
        'hover:bg-[var(--color-accent)] transition-colors',
        'disabled:opacity-50 disabled:pointer-events-none',
      )}
    >
      {children}
    </button>
  );
}

/**
 * MarkdownReadOnly — Renders basic markdown (bold, italic, lists) as safe HTML.
 * No dangerouslySetInnerHTML — uses React elements built from regex parsing.
 */
function MarkdownReadOnly({ text }: { text: string }) {
  if (!text) return <ReadOnlyValue value="—" />;

  const lines = text.split('\n');

  return (
    <div className="text-sm space-y-1 py-1">
      {lines.map((line, i) => {
        // Bullet list
        if (line.match(/^\s*[-*]\s+/)) {
          const content = line.replace(/^\s*[-*]\s+/, '');
          return (
            <div key={i} className="flex gap-2 pl-2">
              <span className="text-[var(--color-muted-foreground)]">&#8226;</span>
              <span>{renderInlineMarkdown(content)}</span>
            </div>
          );
        }
        // Numbered list
        if (line.match(/^\s*\d+\.\s+/)) {
          const match = line.match(/^\s*(\d+)\.\s+(.*)/);
          if (match) {
            return (
              <div key={i} className="flex gap-2 pl-2">
                <span className="text-[var(--color-muted-foreground)] min-w-[1.5em] text-right">
                  {match[1]}.
                </span>
                <span>{renderInlineMarkdown(match[2])}</span>
              </div>
            );
          }
        }
        // Heading
        if (line.match(/^#{1,3}\s+/)) {
          const content = line.replace(/^#{1,3}\s+/, '');
          return (
            <p key={i} className="font-semibold">
              {renderInlineMarkdown(content)}
            </p>
          );
        }
        // Empty line
        if (!line.trim()) {
          return <div key={i} className="h-2" />;
        }
        // Regular paragraph
        return <p key={i}>{renderInlineMarkdown(line)}</p>;
      })}
    </div>
  );
}

/** Render inline markdown (bold, italic) as React elements */
function renderInlineMarkdown(text: string): React.ReactNode {
  // Split on bold (**text**) and italic (*text*) patterns
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold: **text**
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    // Italic: *text*
    const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);

    const boldIndex = boldMatch?.index ?? Infinity;
    const italicIndex = italicMatch?.index ?? Infinity;

    if (boldIndex === Infinity && italicIndex === Infinity) {
      parts.push(remaining);
      break;
    }

    if (boldIndex <= italicIndex && boldMatch) {
      // Text before bold
      if (boldIndex > 0) {
        parts.push(remaining.substring(0, boldIndex));
      }
      parts.push(
        <strong key={key++} className="font-semibold">
          {boldMatch[1]}
        </strong>,
      );
      remaining = remaining.substring(boldIndex + boldMatch[0].length);
    } else if (italicMatch) {
      // Text before italic
      if (italicIndex > 0) {
        parts.push(remaining.substring(0, italicIndex));
      }
      parts.push(
        <em key={key++} className="italic">
          {italicMatch[1]}
        </em>,
      );
      remaining = remaining.substring(italicIndex + italicMatch[0].length);
    }
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

// ─── Read-Only Value Display ───────────────────────────────────────

function ReadOnlyValue({ value }: { value: unknown }) {
  const display = value !== null && value !== undefined ? String(value) : '—';
  return <p className="text-sm py-2">{display}</p>;
}

// ─── Helpers ───────────────────────────────────────────────────────

/** Convert snake_case field name to Title Case label */
export function formatLabel(fieldName: string): string {
  return fieldName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Registry ──────────────────────────────────────────────────────

type FieldRenderer = React.FC<FieldRendererProps>;

/**
 * Map of Directus interface types → React field components.
 * Add new field types by adding one entry here.
 */
const FIELD_REGISTRY: Record<string, FieldRenderer> = {
  // Text inputs
  'input': TextInput,
  'input-multiline': MultilineInput,
  'input-rich-text-md': RichTextMarkdown,
  'input-rich-text-html': RichTextMarkdown,
  'input-code': MultilineInput,

  // Selection
  'select-dropdown': SelectDropdown,
  'select-radio': SelectDropdown,

  // Boolean
  'boolean': BooleanInput,
  'toggle': BooleanInput,

  // Date/time
  'datetime': DateTimeInput,

  // Relational (FIX 2)
  'select-dropdown-m2o': ManyToOneSelect,
  'select-dropdown-m2m': ManyToManySelect,

  // Files (FIX 3)
  'file': FileUploadField,
  'file-image': FileUploadField,
  'files': FileUploadField,

  // Tags
  'tags': TextInput,

  // Slider
  'slider': NumberInput,
};

/**
 * Get the appropriate renderer for a Directus field.
 *
 * Resolution order:
 * 1. meta.interface → exact match in registry
 * 2. field.type → fallback based on database type
 * 3. TextInput as default
 */
export function getFieldRenderer(field: DirectusField): FieldRenderer {
  // Try interface-based lookup first
  const interfaceType = field.meta?.interface;
  if (interfaceType && FIELD_REGISTRY[interfaceType]) {
    return FIELD_REGISTRY[interfaceType];
  }

  // Fallback based on database column type
  // Handles both Directus types and AxonBOS SQL-mapped types
  switch (field.type) {
    case 'boolean':
      return BooleanInput;
    case 'integer':
    case 'bigInteger':
    case 'float':
    case 'decimal':
      return NumberInput;
    case 'date':
      return DateInput;
    case 'dateTime':
    case 'timestamp':
    case 'time':
      return DateTimeInput;
    case 'text':
    case 'json':
      return MultilineInput;
    case 'string':
    case 'uuid':
      return TextInput;
    case 'binary':
      return FileUploadField;
    default:
      return TextInput;
  }
}

/**
 * Register a custom field renderer for a Directus interface type.
 * Use this to extend the platform with custom field widgets.
 */
export function registerFieldRenderer(
  interfaceType: string,
  renderer: FieldRenderer,
) {
  FIELD_REGISTRY[interfaceType] = renderer;
}

// ─── Composite Field Renderer Component ────────────────────────────

interface FieldWrapperProps extends FieldRendererProps {
  /** Show the label (default: true) */
  showLabel?: boolean;
}

/**
 * FieldWrapper — renders a complete form field with label, input, and error.
 *
 * Usage:
 * ```tsx
 * <FieldWrapper field={directusField} value={value} onChange={setValue} />
 * ```
 */
export function FieldWrapper({
  field,
  value,
  onChange,
  error,
  readOnly,
  disabled,
  showLabel = true,
}: FieldWrapperProps) {
  const Renderer = getFieldRenderer(field);
  const isRequired = field.meta?.required ?? false;
  const isBoolean = field.meta?.interface === 'boolean' || field.meta?.interface === 'toggle';

  return (
    <div
      className={cn(
        'space-y-2',
        field.meta?.width === 'half' && 'col-span-1',
        field.meta?.width === 'half-left' && 'col-span-1',
        field.meta?.width === 'half-right' && 'col-span-1',
        (!field.meta?.width || field.meta?.width === 'full' || field.meta?.width === 'fill') &&
          'col-span-2',
      )}
    >
      {showLabel && !isBoolean && (
        <Label className="text-sm font-medium text-[var(--color-foreground)]">
          {formatLabel(field.field)}
          {isRequired && <span className="text-[var(--color-destructive)] ml-1">*</span>}
        </Label>
      )}
      <Renderer
        field={field}
        value={value}
        onChange={onChange}
        error={error}
        readOnly={readOnly}
        disabled={disabled}
      />
      {error && (
        <p className="text-xs text-[var(--color-destructive)]">{error}</p>
      )}
    </div>
  );
}
